// The Products and Stock lists' chosen fields (docs/plans/list-fields.md): the "Fields"
// button and its picker, and one place that turns a field into a column, a cell and a
// phone card's words, so both lists show a field the same way.
import { mulDivRound } from '@ims/core'
import type { ProductRow, RowOf, TenantSettingsRow } from '@ims/data'
import { Button, Checkbox, Modal, cx } from '@ims/ui'
import { LockKeyhole, SlidersHorizontal } from '@ims/ui/icons'
import { useState, type ReactNode } from 'react'
import type { Lookups } from './components.tsx'
import { useFieldGroups, useLive } from './hooks.ts'
import { expiryBadge } from './lib/batches.ts'
import { money, qty, todayIso } from './lib/format.ts'
import {
  LOCKED_FIELDS,
  MAX_FIELDS,
  inListOrder,
  useListFields,
  type FieldId,
  type ListFieldsApi,
  type ListName,
} from './lib/list-fields.ts'
import { useWorkspace } from './workspace.tsx'

/** The list's chosen fields for the open business on this device. */
export function useListFieldsHere(list: ListName): ListFieldsApi {
  const { current } = useWorkspace()
  const { groups } = useFieldGroups()
  return useListFields(list, current?.tenant_id ?? 'none', groups)
}

/** "Fields" beside a list's search box; opens the picker. */
export function FieldsButton({ list, api }: { list: ListName; api: ListFieldsApi }) {
  const [open, setOpen] = useState(false)
  const full = api.fields.length >= MAX_FIELDS
  return (
    <>
      <Button
        variant="secondary"
        aria-label="Fields"
        aria-haspopup="dialog"
        className="shrink-0 max-sm:w-10 max-sm:px-0"
        icon={<SlidersHorizontal className="size-4" />}
        onClick={() => setOpen(true)}
      >
        <span className="max-sm:hidden">Fields</span>
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Show in the list"
        size="sm"
        footer={
          <>
            <Button variant="ghost" className="mr-auto" onClick={api.reset}>
              Reset
            </Button>
            <Button onClick={() => setOpen(false)}>Done</Button>
          </>
        }
      >
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted">Pick up to {MAX_FIELDS} more</span>
          <b className="text-primary">
            {api.fields.length} of {MAX_FIELDS}
          </b>
        </div>
        <PickerGroup title="Always shown">
          {LOCKED_FIELDS[list].map((f) => (
            <li key={f.label} className="flex items-center gap-2.5 px-1 py-1.5 text-subtle">
              <span className="grid size-5 place-items-center rounded-lg bg-soft">
                <LockKeyhole className="size-3.5" />
              </span>
              <FieldName label={f.label} hint={f.hint} />
            </li>
          ))}
        </PickerGroup>
        <PickerGroup title="Your fields">
          {api.offered.map((f) => {
            const on = api.has(f.id)
            return (
              <li key={f.id}>
                <Checkbox
                  className={cx(
                    'flex w-full rounded-lg px-1 py-1.5',
                    on && 'bg-primary-light/50',
                    !on && full && 'cursor-not-allowed text-subtle',
                  )}
                  checked={on}
                  disabled={!on && full}
                  onChange={(e) => api.toggle(f.id, e.target.checked)}
                  label={<FieldName label={f.label} hint={f.hint} />}
                />
              </li>
            )
          })}
        </PickerGroup>
        <p className="mt-3 text-xs text-muted">
          {full ? `That is ${MAX_FIELDS} of ${MAX_FIELDS}: untick one to choose another. ` : ''}
          Only fields your business uses are listed (Settings → Products). A phone shows the first
          two on each card. Saved on this device.
        </p>
      </Modal>
    </>
  )
}

function PickerGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-3">
      <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-subtle">{title}</h3>
      <ul>{children}</ul>
    </section>
  )
}

function FieldName({ label, hint }: { label: string; hint?: string }) {
  return (
    <span className="min-w-0">
      <span className="block font-semibold">{label}</span>
      {hint && <span className="block text-xs font-normal text-muted">{hint}</span>}
    </span>
  )
}

/** The batch to sell first: the earliest expiry among batches with stock. */
export interface NextExpiry {
  batchNo: string
  expiry: string
}

/** Supplier names and next expiries for the rows on screen, read only when a field needs them. */
export function useFieldData(products: ProductRow[], fields: FieldId[]) {
  const wantSuppliers = fields.includes('supplier')
  const wantExpiry = fields.includes('expiry')
  const ids = products.map((p) => p.id)
  const suppliers =
    useLive(
      async (repo) => {
        const out = new Map<string, string>()
        if (!wantSuppliers) return out
        const rows = (await repo.list('suppliers', { limit: 100_000 })).rows
        for (const s of rows) out.set(s.id, s.name)
        return out
      },
      [wantSuppliers],
      ['suppliers'],
    ).data ?? new Map<string, string>()
  const expiry =
    useLive(
      async (repo) => {
        const out = new Map<string, NextExpiry>()
        if (!wantExpiry || ids.length === 0) return out
        const [levels, batches] = await Promise.all([
          repo.stock.batchLevels({ productIds: ids }),
          repo.list('batches', { limit: 100_000 }),
        ])
        const onPage = new Set(ids)
        return nextExpiries(
          batches.rows.filter((b) => onPage.has(b.product_id)),
          levels,
        )
      },
      [wantExpiry, ids.join(',')],
      ['batches', 'stock_movements'],
    ).data ?? new Map<string, NextExpiry>()
  return { suppliers, expiry }
}

/** Per product, the batch with stock that expires first. */
export function nextExpiries(
  batches: RowOf<'batches'>[],
  levels: { batch_id: string; qty_milli: number }[],
): Map<string, NextExpiry> {
  const held = new Map<string, number>()
  for (const l of levels) held.set(l.batch_id, (held.get(l.batch_id) ?? 0) + l.qty_milli)
  const out = new Map<string, NextExpiry>()
  for (const b of batches) {
    if (!b.expiry_date || (held.get(b.id) ?? 0) <= 0) continue
    const first = out.get(b.product_id)
    if (!first || b.expiry_date < first.expiry)
      out.set(b.product_id, { batchNo: b.batch_no, expiry: b.expiry_date })
  }
  return out
}

/** What a field's cell needs about one row. */
export interface FieldRow {
  p: ProductRow
  /** Summed stock (variants included for a group). */
  inStock: number
  tracked: boolean
}

export interface FieldContext {
  lookups: Lookups
  settings: TenantSettingsRow | null | undefined
  suppliers: Map<string, string>
  expiry: Map<string, NextExpiry>
}

const RIGHT: ReadonlySet<FieldId> = new Set(['stock', 'mrp', 'wholesale', 'cost', 'alert', 'value'])

export const FIELD_HEADING: Record<FieldId, string> = {
  category: 'Category',
  stock: 'In stock',
  brand: 'Brand',
  shelf: 'Shelf',
  expiry: 'Next expiry',
  mrp: 'MRP',
  wholesale: 'Wholesale',
  cost: 'Cost',
  unit: 'Unit',
  supplier: 'Supplier',
  barcode: 'Barcode',
  hs: 'HS code',
  tags: 'Tags',
  sku: 'SKU',
  alert: 'Alert at',
  value: 'Stock value',
}

/** A table cell's `align`: numbers right, words left (undefined). */
export const fieldAlign = (id: FieldId): 'right' | undefined =>
  RIGHT.has(id) ? 'right' : undefined

function stockWords(r: FieldRow, ctx: FieldContext): string | null {
  if (!r.tracked) return null
  const unit = ctx.lookups.unit(r.p.unit_id)?.symbol
  return `${qty(r.inStock)}${unit ? ` ${unit}` : ''}`
}

/** A field as plain words (a phone card's line, the stock count in a pill). */
export function fieldText(id: FieldId, r: FieldRow, ctx: FieldContext): string | null {
  const { p } = r
  switch (id) {
    case 'category':
      return ctx.lookups.category(p.category_id)?.name ?? null
    case 'stock':
      return stockWords(r, ctx)
    case 'brand':
      return ctx.lookups.brand(p.brand_id)?.name ?? null
    case 'shelf':
      return p.shelf_location || null
    case 'expiry': {
      const e = ctx.expiry.get(p.id)
      return e ? `${e.batchNo} · exp ${e.expiry}` : null
    }
    case 'mrp':
      return p.mrp_paisa !== null ? `MRP ${money(p.mrp_paisa, ctx.settings)}` : null
    case 'wholesale':
      return p.wholesale_price_paisa !== null
        ? `Wholesale ${money(p.wholesale_price_paisa, ctx.settings)}`
        : null
    case 'cost':
      return p.cost_price_paisa !== null ? `Cost ${money(p.cost_price_paisa, ctx.settings)}` : null
    case 'unit':
      return ctx.lookups.unit(p.unit_id)?.name ?? null
    case 'supplier':
      return (p.supplier_id && ctx.suppliers.get(p.supplier_id)) || null
    case 'barcode':
      return p.barcode || null
    case 'hs':
      return p.hs_code || null
    case 'tags':
      return p.tags || null
    case 'sku':
      return p.sku || null
    case 'alert':
      return p.min_stock_milli ? `Alert at ${qty(p.min_stock_milli)}` : null
    case 'value':
      return p.cost_price_paisa !== null && r.inStock > 0
        ? `Worth ${money(mulDivRound(r.inStock, p.cost_price_paisa, 1000), ctx.settings)}`
        : null
  }
}

/** A field's cell in a desktop row. */
export function fieldCell(id: FieldId, r: FieldRow, ctx: FieldContext): ReactNode {
  const { p } = r
  const none = <span className="text-muted">—</span>
  switch (id) {
    case 'stock':
      return r.tracked ? (
        <span className={cx(r.inStock <= 0 && 'text-danger')}>{stockWords(r, ctx)}</span>
      ) : (
        none
      )
    case 'expiry': {
      const e = ctx.expiry.get(p.id)
      if (!e) return none
      const badge = expiryBadge(e.expiry, todayIso(), ctx.settings?.expiry_alert_days ?? 30)
      return (
        <span className="block leading-tight">
          <span className="block">{e.batchNo}</span>
          <span
            className={cx(
              'block text-xs',
              badge.tone === 'danger'
                ? 'font-semibold text-danger'
                : badge.tone === 'warning'
                  ? 'font-semibold text-warning'
                  : 'text-muted',
            )}
          >
            exp {e.expiry}
          </span>
        </span>
      )
    }
    case 'mrp':
      return p.mrp_paisa !== null ? money(p.mrp_paisa, ctx.settings) : none
    case 'wholesale':
      return p.wholesale_price_paisa !== null ? money(p.wholesale_price_paisa, ctx.settings) : none
    case 'cost':
      return p.cost_price_paisa !== null ? money(p.cost_price_paisa, ctx.settings) : none
    case 'alert':
      return p.min_stock_milli ? qty(p.min_stock_milli) : none
    case 'value':
      return p.cost_price_paisa !== null && r.inStock > 0
        ? money(mulDivRound(r.inStock, p.cost_price_paisa, 1000), ctx.settings)
        : none
    default:
      return fieldText(id, r, ctx) ?? none
  }
}

/** The chosen fields that get a column of their own (the SKU stays under the name). */
export const columnFields = (fields: FieldId[]): FieldId[] => fields.filter((f) => f !== 'sku')

/** A phone card's line: the first two chosen fields with something to say, in list order. */
export function phoneLine(
  list: ListName,
  fields: FieldId[],
  r: FieldRow,
  ctx: FieldContext,
): string[] {
  return inListOrder(list, fields)
    .map((f) => fieldText(f, r, ctx))
    .filter((t): t is string => !!t)
    .slice(0, 2)
}
